import { Routes } from '@angular/router';
import { saphirAuthGuard, saphirCallbackRoute } from '@saphir/auth-client';

import { environment } from '../environments/environment';
import { MODULE_ROUTE_PREFIX } from './core/modules/module-routes';
import { NO_LICENCE_SEGMENT } from './core/modules/module-url';
import { Dashboard } from './features/dashboard/dashboard';
import { ModuleInfo } from './features/module-info/module-info';
import { ModuleNoLicence } from './features/module-no-licence/module-no-licence';
import { ModuleUnavailable } from './features/module-unavailable/module-unavailable';
import { NotFound } from './features/not-found/not-found';

/**
 * The shell's own routes.
 *
 * Modules are children of the list they are picked from, not siblings of it:
 * that is what makes `SAPHIR Web › Modules › Payroll › Employee` fall out of
 * the router instead of being assembled by hand in the header.
 *
 * One route per installed module is inserted into `modules` during application
 * start (see `provideModuleRegistry`), ahead of the `:moduleId` route, which
 * therefore only ever catches ids that produced none: unknown, disabled, or
 * failed to resolve.
 *
 * `title` is a translation key — `TranslatedTitleStrategy` resolves it.
 */
const SHELL_ROUTES: Routes = [
  { path: '', pathMatch: 'full', redirectTo: MODULE_ROUTE_PREFIX },
  {
    path: MODULE_ROUTE_PREFIX,
    title: 'dashboard.title',
    children: [
      { path: '', pathMatch: 'full', component: Dashboard },
      // Service page of one module (version, UMC module, licence — for
      // support). Static and ahead of `:moduleId`; no module claims the id
      // `info`, so the segment is safe to reserve.
      { path: 'info/:moduleId', component: ModuleInfo, title: 'module.info.title' },
      // Where a module withheld by licensing leads (`licenseGuard`): which company lacks what,
      // and the way to UMC. Static and ahead of `:moduleId`, like `info`.
      {
        path: `${NO_LICENCE_SEGMENT}/:moduleId`,
        component: ModuleNoLicence,
        title: 'module.noLicence.tab',
      },
      { path: ':moduleId', component: ModuleUnavailable, title: 'module.unavailable.title' },
    ],
  },
  { path: '**', component: NotFound, title: 'notFound.title' },
];

/**
 * The routes the application actually mounts, shaped by the auth mode.
 *
 * With real sign-in, everything sits behind `saphirAuthGuard` — except the
 * callback route, which must stay OUTSIDE the guard: a guard on the callback
 * would start a new sign-in to complete the one already in progress, which
 * loops. The redirect route needs no guard of its own — it only forwards to a
 * guarded destination.
 *
 * Exported as a function so specs can exercise both shapes; the application
 * uses `routes`, built once for the current environment.
 */
export function buildAppRoutes(useMockAuth: boolean): Routes {
  if (useMockAuth) {
    return SHELL_ROUTES;
  }

  return [
    saphirCallbackRoute(),
    ...SHELL_ROUTES.map((route) =>
      route.redirectTo !== undefined ? route : { ...route, canActivate: [saphirAuthGuard] },
    ),
  ];
}

export const routes: Routes = buildAppRoutes(environment.auth.useMockAuth);
